import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { truncateString } from "utils/UtilityFuncs";
import useInbox from "../../InboxTab/InboxContext";
import EmailBulkActions from "../EmailBulkActions";
import "./emailsList.scss";

export default function EmailsList({ searchQuery }) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();

  const {
    labels,
    defaultLabels,
    selectedLabel,
    formatGmailDate,
    handleBulkReadStatus,
    handleBulkDelete,
    getFilteredLabelNames,
    pageTokensList,
    setPageTokensList,
    emailsList,
    setEmailsList,
    refreshEmails,
    setRefreshEmails,
    setFromEmail,
    getSenderName,
    setMessageId,
  } = useInbox();

  const [loading, setLoading] = useState(false);

  const [companyEmail, setCompanyEmail] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [showUnread, setShowUnread] = useState(false);

  const displayedEmails = useMemo(
    () =>
      showUnread ? emailsList.filter((email) => email?.unread) : emailsList,
    [emailsList, showUnread],
  );

  /* Pagination ********* */

  const initialQueryValue = searchQuery || "";

  const [limit, setLimit] = useState(20);
  const [previousToken, setPreviousToken] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [query, setQuery] = useState(initialQueryValue);
  const [submittedQuery, setSubmittedQuery] = useState(initialQueryValue);
  const submittedQueryRef = useRef(initialQueryValue);
  const [paginationHistory, setPaginationHistory] = useState([null]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  /*   All Functions ****************************** */

  const getLabelBadgeClass = (labelName) => {
    const value = (labelName || "").toLowerCase();

    if (value === "inbox") return "bg-secondary";
    if (value === "sent") return "bg-sent";
    if (value === "archived_mails") return "bg-primary";
    if (value === "archived") return "bg-primary";
    if (value === "trash") return "bg-danger";
    if (value === "spam") return "bg-danger";

    return "bg-info";
  };

  const emailRowBgColor = (email) => {
    let labels = getFilteredLabelNames(email?.folders);

    if (labels.includes("Sent")) {
      return `sent-row ${email?.unread ? "unread" : "read"}`;
    }

    return (
      selectedEmails.includes(email?.id) ?
        "bg-primary bg-opacity-25 email-hover"
      : email?.unread ? "bg-white  email-hover"
      : "bg-light bg-opacity-50 email-hover"
    );
  };

  const getEmailsByLabel = useCallback(
    async ({ clearSearch = false, query: queryParam, pageToken } = {}) => {
      try {
        setLoading(true);
        const usedQuery =
          clearSearch ? ""
          : typeof queryParam !== "undefined" ? queryParam
          : submittedQueryRef.current || "";

        let folderIds = [
          selectedLabel === "ALL_MAIL" ? "ARCHIVED" : selectedLabel,
        ];

        let APIPATH = "/emails/by-labels?limit=" + limit;
        if (searchQuery) {
          APIPATH = "/emails/by-search-query?limit=" + limit;
        }

        const { data } = await API.post(
          APIPATH,
          {
            query: searchQuery ? searchQuery : usedQuery,
            folderIds: searchQuery ? null : folderIds,
            isArchived: selectedLabel === "ALL_MAIL" ? true : null,
            pageToken: pageToken || null,
            // showUnread removed; handled client-side
          },
          { headers: authHeader() },
        );

        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages }
          // setEmailsList(data?.emails || []);
          setEmailsList(data?.data?.data || []);
          setSelectedEmails([]);

          setCompanyEmail(data?.companyEmail || null);
          let receivedPageToken = data?.data?.nextCursor || null;
          handlePagination(receivedPageToken, pageToken);
          // setTotalPages(data?.totalPages || 0);
          // setTotalResult(data?.total || 0);
          // refreshLabels();
          setRefreshEmails(false);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            }),
          );
        }
      } catch (err) {
        dispatch(
          setNotification({
            message: err?.response?.data.message || err.message,
            type: "error",
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [limit, selectedLabel, dispatch, searchQuery, refreshEmails],
  );

  const handleSearchSubmit = useCallback(
    (event) => {
      event.preventDefault();
      const trimmedQuery = (query || "").trim();
      setSelectedEmails([]);

      if (!trimmedQuery) {
        setSubmittedQuery("");
        getEmailsByLabel({ clearSearch: true });
        return;
      }

      setSubmittedQuery(trimmedQuery);
      getEmailsByLabel({ query: trimmedQuery });
    },
    [query, getEmailsByLabel],
  );

  const handlePagination = (receivedPageToken, currentPageToken) => {
    // Update next token
    setNextToken(receivedPageToken);
    setPreviousToken(currentPageToken);

    // add received token to page tokens list if not already present
    let updatedTokensList = [...pageTokensList];
    if (receivedPageToken && !updatedTokensList.includes(receivedPageToken)) {
      updatedTokensList.push(receivedPageToken);
    }

    setPageTokensList(updatedTokensList);

    setPaginationHistory((prevHistory) => {
      const tokenToTrack = currentPageToken ?? null;
      if (!prevHistory.length) {
        setCurrentPageIndex(0);
        return [tokenToTrack];
      }

      const existingIndex = prevHistory.indexOf(tokenToTrack);
      if (existingIndex === -1) {
        const nextHistory = [...prevHistory, tokenToTrack];
        setCurrentPageIndex(nextHistory.length - 1);
        return nextHistory;
      }

      setCurrentPageIndex(existingIndex);
      return prevHistory;
    });
  };

  const syncEmails = async (opt = {}) => {
    try {
      if (!selectedLabel) {
        dispatch(
          setNotification({ type: "error", message: "Please select a label" }),
        );
        return;
      }
      setLoading(true);

      const { data } = await API.post(
        `/emails/sync-emails/${selectedLabel}`,
        {},
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        // refreshLabels();
        await getEmailsByLabel({
          limit: limit,
          query: submittedQuery,
          page: 1,
        });
      } else if (!data.success) {
        dispatch(setNotification({ type: "error", message: data.message }));
      } else {
        dispatch(
          setNotification({
            type: "error",
            message: "Something went wrong CheckLogs",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  // const getSenderName = (email) => {
  //   let sender = email?.from[0]?.name || "";

  //   if (email?.from[0]?.email === companyEmail) {
  //     sender = sender !== "" ? sender + " (me)" : "me";
  //   } else if (sender === "") {
  //     sender = email?.from[0]?.email || "Unknown Sender";
  //   }

  //   return sender;
  // };

  const handleAllCheck = (e) => {
    if (e.target.checked) {
      setSelectedEmails(displayedEmails.map((email) => email?.id));
    } else {
      setSelectedEmails([]);
    }
  };

  const handleEmailCheck = (emailId) => {
    let updatedSelectedEmails = [...selectedEmails];
    if (updatedSelectedEmails.includes(emailId)) {
      updatedSelectedEmails = updatedSelectedEmails.filter(
        (id) => id !== emailId,
      );
    } else {
      updatedSelectedEmails.push(emailId);
    }
    setSelectedEmails(updatedSelectedEmails);
  };

  const handleEmailReadStatus = async (messageId, isRead) => {
    try {
      if (!messageId) {
        return dispatch(
          setNotification({
            message: "Invalid email selected",
            type: "error",
          }),
        );
      }

      setLoading(true);
      let success = await handleBulkReadStatus([messageId], isRead);
      if (success) {
        setSelectedEmails([]);
        // refreshLabels();
        await getEmailsByLabel();
        dispatch(
          setNotification({
            message: `Email marked as ${isRead ? "read" : "unread"}`,
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: "Failed to update email status",
            type: "error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      if (!messageId) {
        return dispatch(
          setNotification({
            message: "Invalid email selected",
            type: "error",
          }),
        );
      }

      setLoading(true);
      let success = await handleBulkDelete([messageId]);
      if (success) {
        setSelectedEmails([]);
        // refreshLabels();
        await getEmailsByLabel();
      } else {
        dispatch(
          setNotification({
            message: "Failed to delete email",
            type: "error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const paginationInfo = useMemo(() => {
    const labelObj = [...labels, ...defaultLabels].find(
      (label) => label.id === selectedLabel,
    );
    const totalEmailsCount = labelObj?.totalCount ?? 0;
    if (!totalEmailsCount || emailsList.length === 0) {
      return "";
    }

    const totalPages = Math.max(Math.ceil(totalEmailsCount / limit), 1);
    const currentPageNumber = Math.min(currentPageIndex + 1, totalPages);
    const startIndex = (currentPageNumber - 1) * limit + 1;
    const endIndex = Math.min(
      startIndex + emailsList.length - 1,
      totalEmailsCount,
    );
    return `${startIndex}-${endIndex} of ${totalEmailsCount}`;
  }, [
    defaultLabels,
    emailsList,
    labels,
    limit,
    selectedLabel,
    currentPageIndex,
  ]);

  /*   All UseEffects ************************* */

  useEffect(() => {
    submittedQueryRef.current = submittedQuery;
  }, [submittedQuery]);

  useEffect(() => {
    if (typeof searchQuery === "undefined") {
      return;
    }
    setQuery(searchQuery || "");
    setSubmittedQuery(searchQuery || "");
  }, [searchQuery]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (!selectedLabel) {
        return;
      }
      setSelectedEmails([]);

      if (refreshEmails) {
        setRefreshEmails(false);
        const activeQuery = submittedQueryRef.current;
        if (activeQuery) {
          getEmailsByLabel({ query: activeQuery });
        } else {
          getEmailsByLabel({ clearSearch: true });
        }
      } else {
        if (searchQuery) {
          getEmailsByLabel({ query: searchQuery });
          return;
        }

        const activeQuery = submittedQueryRef.current;
        if (activeQuery) {
          getEmailsByLabel({ query: activeQuery });
        } else {
          getEmailsByLabel({ clearSearch: true });
        }
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [limit, selectedLabel, searchQuery, getEmailsByLabel, refreshEmails]);

  // useEffect(() => {
  //   let timeout = setTimeout(() => {
  //     if (refreshEmails) {
  //       setSelectedEmails([]);
  //       const activeQuery = submittedQueryRef.current;
  //       if (activeQuery) {
  //         getEmailsByLabel({ query: activeQuery });
  //       } else {
  //         getEmailsByLabel({ clearSearch: true });
  //       }
  //     }
  //   }, 50);
  //   return () => clearTimeout(timeout);
  // }, [refreshEmails, getEmailsByLabel]);

  useEffect(() => {
    setPaginationHistory([null]);
    setCurrentPageIndex(0);
  }, [selectedLabel, limit, submittedQuery]);

  if (!selectedLabel) {
    return (
      <main className="flex-grow-1 px-3 d-flex justify-content-center align-items-center">
        <div className="text-center text-muted">
          <h5>Please select a label to view emails</h5>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow-1 px-3">
      {/*   Search Bar
       ********************************************* */}
      <div
        className="d-flex justify-content-between align-items-center my-2"
        style={{
          height: "5vh",
          overflowY: "auto",
        }}
      >
        <div className="w-75">
          <div className="d-flex align-items-center gap-2">
            {!searchQuery && (
              <form
                onSubmit={handleSearchSubmit}
                className="flex-grow-1 d-flex align-items-center gap-1"
              >
                <input
                  type="search"
                  value={query ?? ""}
                  onChange={(e) => {
                    if (e.target.value === "") {
                      setSubmittedQuery("");
                      getEmailsByLabel({ clearSearch: true });
                    }
                    setQuery(e.target.value);
                  }}
                  className="form-control rounded-pill bg-white border-0 shadow-sm px-3"
                  placeholder="Search..."
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-sm rounded-pill px-3"
                  disabled={loading}
                >
                  Search
                </button>
              </form>
            )}
            {/* <AdvanceEmailSearch /> */}
            <button
              onClick={(e) => {
                setShowUnread((prev) => !prev);
              }}
              className={`btn px-3 btn-sm btn-${
                showUnread ? "primary" : "outline-primary"
              }  rounded-pill`}
            >
              Unread
            </button>
          </div>
        </div>
        <div className="w-50 text-end px-2">
          {!searchQuery && (
            <button
              onClick={(e) => {
                syncEmails();
              }}
              disabled={loading}
              className="btn btn-info btn-sm me-2"
            >
              <i className="mdi mdi-cloud me-1"></i>
              Sync Emails
            </button>
          )}
          <button
            onClick={(e) => {
              getEmailsByLabel();
            }}
            className="btn btn-success btn-sm"
          >
            <i className="mdi mdi-refresh me-1"></i>
            Refresh
          </button>
        </div>
      </div>

      <div
        className="card custom-border-radius p-2"
        style={{
          height: "91vh",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {/*   Select All And Bulk Actions
         ********************************************* */}
        <div className="d-flex justify-content-between align-items-center mb-2 ps-1 pe-2">
          <div className="w-75">
            <div className="d-flex align-items-center gap-2">
              <input
                type="checkbox"
                style={{
                  marginBottom: 2,
                }}
                checked={
                  displayedEmails.length > 0 &&
                  selectedEmails.length === displayedEmails.length
                }
                onChange={handleAllCheck}
                className="form-check-input"
              />

              {selectedEmails.length > 0 && (
                <EmailBulkActions
                  refreshFunc={getEmailsByLabel}
                  refreshFuncParams={{ limit: limit, query: submittedQuery }}
                  selectedEmails={selectedEmails}
                />
              )}
            </div>
          </div>
          {/* ******************** Pagination ************************* */}
          <div>
            <div className="d-flex align-items-center">
              {/* Limit Select Drop Down */}
              <Dropdown>
                <Dropdown.Toggle
                  as={"div"}
                  style={{ cursor: "pointer" }}
                  className="no-icon d-inline-block"
                  variant=""
                >
                  <span className="me-2">Limit: {limit}</span>
                  <i className="mdi mdi-chevron-down fs-4"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      setLimit(10);
                    }}
                  >
                    10
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setLimit(25);
                    }}
                  >
                    25
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setLimit(50);
                    }}
                  >
                    50
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              {!searchQuery && selectedLabel && paginationInfo}

              {/* Pagination buttons */}
              <button
                onClick={(e) => {
                  let _previousToken = null;
                  if (pageTokensList?.length > 1 && previousToken) {
                    const currentIndex = pageTokensList.indexOf(previousToken);
                    if (currentIndex > 0) {
                      _previousToken = pageTokensList[currentIndex - 1];
                    }
                  }

                  if (!previousToken) {
                    setPageTokensList([]);
                  }

                  const activeQuery = submittedQueryRef.current;
                  if (activeQuery) {
                    getEmailsByLabel({
                      pageToken: _previousToken,
                      limit: limit,
                      query: activeQuery,
                    });
                  } else {
                    getEmailsByLabel({
                      pageToken: _previousToken,
                      limit: limit,
                      clearSearch: true,
                    });
                  }
                }}
                disabled={!previousToken}
                className="btn btn-sm px-1 py-0 border-0"
                title="previous page"
              >
                <i className="mdi mdi-chevron-left fs-3 "></i>
              </button>
              <button
                onClick={(e) => {
                  const activeQuery = submittedQueryRef.current;
                  if (activeQuery) {
                    getEmailsByLabel({
                      pageToken: nextToken,
                      limit: limit,
                      query: activeQuery,
                    });
                  } else {
                    getEmailsByLabel({
                      pageToken: nextToken,
                      limit: limit,
                      clearSearch: true,
                    });
                  }
                }}
                disabled={!nextToken}
                className="btn btn-sm px-1 py-0 border-0"
                title="next page"
              >
                <i className="mdi mdi-chevron-right fs-3 "></i>
              </button>
            </div>
          </div>
        </div>
        <hr className="mt-0 mb-0" />

        {/*   Emails List
         ********************************************* */}
        <div>
          <table className="table table-sm">
            <tbody>
              {loading ?
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              : displayedEmails?.length > 0 || searchQuery ?
                displayedEmails?.map((email) => (
                  <tr
                    className={`email-row border-start border-end ${emailRowBgColor(
                      email,
                    )}`}
                    key={email.id}
                    style={{
                      cursor: "pointer",
                      overflow: "hidden",
                    }}
                    onClick={() => {
                      // setMessageId(email.id);
                      const primaryFrom = email?.from?.[0]?.email || "";
                      const primaryTo = email?.to?.[0]?.email || "";

                      const historyEmail =
                        selectedLabel === "SENT" ? primaryTo : primaryFrom;

                      setFromEmail(historyEmail || null);
                    }}
                  >
                    <td
                      style={{ width: "30px" }}
                      onClick={(e) => e.stopPropagation()}
                      className=""
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmails.includes(email?.id)}
                        onChange={() => handleEmailCheck(email?.id)}
                        className="form-check-input"
                      />
                    </td>
                    <td className="text-truncate" style={{ maxWidth: "280px" }}>
                      <span
                        className={`text-mute-2 d-flex align-items-center ${
                          email?.unread ? "fw-bold" : ""
                        }`}
                      >
                        {getSenderName(email, companyEmail)}
                      </span>
                    </td>
                    <td>
                      <div
                        className="text-truncate "
                        style={{ maxWidth: "550px" }}
                      >
                        {selectedLabel === "INBOX" && (
                          <span className="ms-3">
                            {getFilteredLabelNames(email?.folders).map(
                              (labelName) => (
                                <span
                                  key={labelName}
                                  className={`badge ${getLabelBadgeClass(
                                    labelName,
                                  )} fs-6 text-white me-1`}
                                >
                                  {labelName === "archived_mails" ?
                                    "Archived"
                                  : labelName}
                                </span>
                              ),
                            )}
                          </span>
                        )}
                        <span
                          className={`text-mute-2 ${
                            email?.unread ? "fw-bold" : ""
                          }`}
                        >
                          {email?.subject}
                        </span>{" "}
                        - {truncateString(email?.snippet, 100)}
                      </div>
                    </td>
                    <td
                      className={`d-none d-md-table-cell  small ${
                        email?.unread ? "fw-bold text-mute-2" : "text-muted"
                      }`}
                    >
                      {formatGmailDate(email?.date)}
                    </td>
                    <td
                      className="email-row__action-cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="email-row__action-panel">
                        {/* View Icon */}
                        <button
                          type="button"
                          className="email-row__action"
                          onClick={(event) => {
                            event.stopPropagation();
                            setMessageId(email.id);
                          }}
                          title="View email"
                          aria-label="View email"
                        >
                          <i
                            className="mdi mdi-eye-outline email-row__action"
                            title="View email"
                            aria-label="View email"
                          ></i>
                        </button>

                        {selectedLabel !== "TRASH" && (
                          <button
                            type="button"
                            className="email-row__action"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDeleteMessage(email?.id);
                            }}
                            disabled={loading}
                            title="Delete email"
                            aria-label="Delete email"
                          >
                            <i className="mdi mdi-trash-can-outline"></i>
                          </button>
                        )}
                        <button
                          type="button"
                          className="email-row__action"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEmailReadStatus(email?.id, false);
                          }}
                          disabled={loading}
                          title="Mark as unread"
                          aria-label="Mark as unread"
                        >
                          <i className="mdi mdi-email-outline"></i>
                        </button>
                        <button
                          type="button"
                          className="email-row__action"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEmailReadStatus(email?.id, true);
                          }}
                          disabled={loading}
                          title="Mark as read"
                          aria-label="Mark as read"
                        >
                          <i className="mdi mdi-email-open-outline"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              : <tr>
                  <td colSpan="5" className="text-center py-4 text-muted">
                    No emails found
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}
